import { ref, computed } from 'vue';
import { useToast } from 'primevue/usetoast';
import { useQueryClient } from '@tanstack/vue-query';
import {
  usePostFetchAllArticles,
  useGetFetchArticle,
  getGetFetchArticleQueryKey,
  usePostCreateArticle,
  usePatchPatchArticle,
  usePostUpdateArticleOptionStatus,
  usePostCreateArticleOption,
  usePatchPatchArticleOption,
  usePostFetchAllSupplier,
} from 'src/api/queries/order-portal-bff';
import type { FetchArticleResponseLite } from 'src/api/models/fetchArticleResponseLite';
import type { FetchArticleResponse } from 'src/api/models/fetchArticleResponse';
import type { ArticleFiltersDTO } from 'src/api/models/articleFiltersDTO';
import type { CreateArticleDTO } from 'src/api/models/createArticleDTO';
import type { PatchArticleDTO } from 'src/api/models/patchArticleDTO';
import { UpdateArticleStatusDTOStatus } from 'src/api/models/updateArticleStatusDTOStatus';
import { ArticleFiltersDTOStatus } from 'src/api/models/articleFiltersDTOStatus';
import type { CreateArticleOptionDTO } from 'src/api/models';
import type { PatchArticleOptionDTO } from 'src/api/models/patchArticleOptionDTO';

let globalLoadRequestId = 0;

export function useArticleManagement () {
  const toast = useToast();
  const queryClient = useQueryClient();

  // Article list state
  const articles = ref<FetchArticleResponseLite[]>([]);
  const loading = ref(false);
  const page = ref(1);
  const totalPages = ref(1);
  const totalRecords = ref(0);

  // Search & filter state
  const searchQuery = ref('');
  const statusFilter = ref<ArticleFiltersDTOStatus | null>(null);

  // Selected article detail
  const selectedArticleId = ref<string | null>(null);
  const operationInProgress = ref(false);

  // Mutation hooks
  const { mutateAsync: fetchAllArticles } = usePostFetchAllArticles();
  const { mutateAsync: createArticleMutation } = usePostCreateArticle();
  const { mutateAsync: patchArticleMutation } = usePatchPatchArticle();
  const { mutateAsync: createArticleOptionMutation } = usePostCreateArticleOption();
  const { mutateAsync: patchArticleOptionMutation } = usePatchPatchArticleOption();
  const { mutateAsync: updateStatusMutation } = usePostUpdateArticleOptionStatus();
  const { mutateAsync: fetchSuppliers } = usePostFetchAllSupplier();

  // Query for selected article detail
  const selectedArticleQuery = useGetFetchArticle(
    computed(() => selectedArticleId.value ?? ''),
    {
      query: {
        enabled: computed(() => !!selectedArticleId.value),
        placeholderData: (previousData: FetchArticleResponse | undefined) => previousData,
      },
    },
  );

  const selectedArticle = computed<FetchArticleResponse | null>(
    () => selectedArticleQuery.data?.value ?? null,
  );
  const selectedArticleLoading = computed(() => selectedArticleQuery.isFetching?.value ?? false);

  function buildFilters (): ArticleFiltersDTO {
    const filters: ArticleFiltersDTO = {};
    const q = searchQuery.value.trim();
    if (q) {
      filters.name = q;
      filters.match_mode = 'or' as ArticleFiltersDTO['match_mode'];
      filters.code = q;
      filters.brand_name = q;
      filters.external_reference = q;
      filters.location = q;
      filters.description = q;
    }
    if (statusFilter.value) {
      filters.status = statusFilter.value;
    }
    return filters;
  }

  async function loadArticles (targetPage = 1) {
    const thisRequest = ++globalLoadRequestId;
    loading.value = true;
    try {
      const response = await fetchAllArticles({
        data: buildFilters(),
        params: { page: targetPage, limit: 25 },
      });
      if (thisRequest !== globalLoadRequestId) return;
      articles.value = response.data ?? [];
      page.value = response.page ?? 1;
      const responseTotalPages = (response as Record<string, unknown>).total_pages;
      totalPages.value = typeof responseTotalPages === 'number' ? responseTotalPages : 1;
      const responseTotalRecords = (response as Record<string, unknown>).total_records;
      totalRecords.value = typeof responseTotalRecords === 'number' ? responseTotalRecords : (totalPages.value * 25);
    } catch {
      if (thisRequest !== globalLoadRequestId) return;
      toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to load articles', life: 3000 });
    } finally {
      if (thisRequest === globalLoadRequestId) loading.value = false;
    }
  }

  function selectArticle (id: string | null) {
    selectedArticleId.value = id;
  }

  async function refreshSelectedArticle () {
    if (selectedArticleId.value) {
      queryClient.invalidateQueries({ queryKey: getGetFetchArticleQueryKey(selectedArticleId.value) });
    }
  }

  async function updateArticle (id: string, data: PatchArticleDTO) {
    operationInProgress.value = true;
    try {
      await patchArticleMutation({ id, data });
      toast.add({ severity: 'success', summary: 'Success', detail: 'Article updated', life: 3000 });
      await refreshSelectedArticle();
      await loadArticles(page.value);
    } catch {
      toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to update article', life: 3000 });
    } finally {
      operationInProgress.value = false;
    }
  }

  async function createArticle (data: CreateArticleDTO): Promise<string | null> {
    operationInProgress.value = true;
    try {
      const newId = await createArticleMutation({ data });
      toast.add({ severity: 'success', summary: 'Success', detail: 'Article created', life: 3000 });
      await loadArticles(page.value);
      if (newId) {
        selectedArticleId.value = newId;
      }
      return newId ?? null;
    } catch {
      toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to create article', life: 3000 });
      throw new Error('Create failed');
    } finally {
      operationInProgress.value = false;
    }
  }

  async function toggleArticleStatus (id: string, currentStatus: string) {
    operationInProgress.value = true;
    try {
      const newStatus = currentStatus === 'active'
        ? UpdateArticleStatusDTOStatus.inactive
        : UpdateArticleStatusDTOStatus.active;
      await updateStatusMutation({ id, data: { status: newStatus } });
      toast.add({
        severity: 'success',
        summary: 'Success',
        detail: `Article ${newStatus === 'active' ? 'activated' : 'deactivated'}`,
        life: 3000,
      });
      await refreshSelectedArticle();
      await loadArticles(page.value);
    } catch {
      toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to update status', life: 3000 });
    } finally {
      operationInProgress.value = false;
    }
  }

  async function setPreferredOption (articleId: string, optionId: string) {
    operationInProgress.value = true;
    try {
      await patchArticleMutation({ id: articleId, data: { preferred_article_option_id: optionId } as PatchArticleDTO });
      toast.add({ severity: 'success', summary: 'Success', detail: 'Preferred option updated', life: 3000 });
      await refreshSelectedArticle();
      await loadArticles(page.value);
    } catch {
      toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to set preferred option', life: 3000 });
    } finally {
      operationInProgress.value = false;
    }
  }

  async function addArticleOption (articleId: string, data: CreateArticleOptionDTO) {
    operationInProgress.value = true;
    try {
      await createArticleOptionMutation({ data: { ...data, article_id: articleId } });
      toast.add({ severity: 'success', summary: 'Success', detail: 'Article option added', life: 3000 });
      await refreshSelectedArticle();
    } catch {
      toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to add article option', life: 3000 });
    } finally {
      operationInProgress.value = false;
    }
  }

  async function updateArticleOption (optionId: string, data: PatchArticleOptionDTO) {
    operationInProgress.value = true;
    try {
      await patchArticleOptionMutation({ id: optionId, data });
      toast.add({ severity: 'success', summary: 'Success', detail: 'Article option updated', life: 3000 });
      await refreshSelectedArticle();
    } catch {
      toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to update article option', life: 3000 });
    } finally {
      operationInProgress.value = false;
    }
  }

  async function removeArticleOption (_articleId: string, _optionId: string) {
    toast.add({ severity: 'warn', summary: 'Not available', detail: 'Remove article option endpoint not yet available', life: 3000 });
  }

  async function searchSuppliers (_query: string, pageNum: number) {
    const response = await fetchSuppliers({
      params: { page: pageNum, limit: 20 },
    });
    const raw = response as Record<string, unknown>;
    return {
      data: response.data ?? [],
      page: typeof raw.page === 'number' ? raw.page : 1,
      total_pages: typeof raw.total_pages === 'number' ? raw.total_pages : 1,
    };
  }

  return {
    articles,
    loading,
    page,
    totalPages,
    totalRecords,
    searchQuery,
    statusFilter,
    selectedArticleId,
    selectedArticle,
    selectedArticleLoading,
    operationInProgress,
    loadArticles,
    selectArticle,
    updateArticle,
    createArticle,
    toggleArticleStatus,
    setPreferredOption,
    addArticleOption,
    updateArticleOption,
    removeArticleOption,
    refreshSelectedArticle,
    searchSuppliers,
  };
}
